import { useRef } from 'react';
import { Modal as AntModal, ConfigProvider } from 'antd';
import { Button } from '../buttons';
import { Divider } from '../separators';
import styles from './modal.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { colorPalette } from 'shared/constants';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';

/**
 * @typedef {import('antd').ModalProps} AntModalProps
 */

/**
 * @typedef {AntModalProps & {
 *   onSubmit?: Function,
 *   cancelText?: string,
 *   submitText?: string,
 *   loading?: boolean,
 *   submitDisabled?: boolean,
 *   submitButtonType?: import('antd').ButtonProps['type'],
 *   submitDanger?: boolean,
 *   footerAlign?: 'space-between' | 'end'
 * }} ModalProps
 */

/**
 * Modal Component
 *
 * A reusable modal dialog based on Ant Design Modal with consistent styling
 * and button placement following the design system standards.
 *
 * Features:
 * - Consistent header, body, and footer layout
 * - Automatic footer with cancel and submit buttons
 * - Custom footer support
 * - Loading states
 * - Configurable button labels and actions
 * - Focus restoration on close
 * - Pass-through support for all Ant Design Modal props
 *
 * @component
 * @param {ModalProps} props - Component props
 * @returns {React.ReactNode} The modal component
 */
export function Modal({
  open,
  title,
  children,
  onCancel,
  onSubmit,
  cancelText = 'Cancel',
  submitText = 'Submit',
  loading = false,
  submitDisabled = false,
  submitButtonType = 'primary',
  submitDanger = false,
  footerAlign = 'space-between',
  footer,
  width = 500,
  centered = true,
  destroyOnHidden = true,
  className = '',
  ...rest
}) {
  const containerRef = useRef(null);

  // Render popups inside the modal for screen reader accessibility
  // By default, antd renders Select/DatePicker dropdowns as portals to document.body
  // This breaks screen readers because the modal has aria-modal="true" which
  // tells screen readers to ignore content outside the modal
  const getPopupContainer = () => containerRef.current || document.body;

  const renderDefaultFooter = () => (
    <>
      <Divider />
      <div className={classNames(styles.footer, { [styles.footerEnd]: footerAlign === 'end' })}>
        <Button type="default" onClick={onCancel} disabled={loading}>
          {cancelText}
        </Button>
        {onSubmit && (
          <Button
            type={submitButtonType}
            onClick={onSubmit}
            loading={loading}
            disabled={submitDisabled || loading}
            danger={submitDanger}
            htmlType="submit">
            {submitText}
          </Button>
        )}
      </div>
    </>
  );

  return (
    <AntModal
      className={`new-design-system ${className}`}
      open={open}
      title={<h2 className={styles.title}>{title}</h2>}
      onCancel={onCancel}
      footer={footer !== undefined ? footer : renderDefaultFooter()}
      destroyOnHidden={destroyOnHidden}
      width={width}
      centered={centered}
      // Make sure the Escape key or the close icon disabled when loading
      keyboard={!loading}
      closable={!loading}
      focusable={{ trap: true, focusTriggerAfterClose: true }}
      closeIcon={<FontAwesomeIcon icon={faXmark} color={colorPalette.primaryMain8} size="lg" />}
      {...rest}>
      <div ref={containerRef} className={styles.container}>
        <ConfigProvider getPopupContainer={getPopupContainer}>{children}</ConfigProvider>
      </div>
    </AntModal>
  );
}
